import { useMemo, useState } from 'react';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { usePreRegisteredAccommodations } from './usePreRegistrationQueries';
import type {
  AccommodationFilterValues,
  PreRegisteredAccommodationExportParams,
  PreRegisteredAccommodationSearchParams,
} from '../types/pre-registration.types';

export const ACCOMMODATIONS_PAGE_SIZE = 20;

export const EMPTY_ACCOMMODATION_FILTERS: AccommodationFilterValues = {
  search: '',
  includeDeleted: false,
};

// Filter + pagination state of the pre-registered accommodations page
export const usePreRegisteredAccommodationSearch = () => {
  const [filters, setFilters] = useState<AccommodationFilterValues>(EMPTY_ACCOMMODATION_FILTERS);
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebouncedValue(filters.search.trim());

  // Filters without pagination: also the body of the Excel export
  const filterParams = useMemo<PreRegisteredAccommodationExportParams>(
    () => ({
      search: debouncedSearch || undefined,
      municipalityId: filters.municipalityId,
      localityId: filters.localityId,
      statusIds: filters.statusId !== undefined ? [filters.statusId] : undefined,
      // statusIds wins over includeDeleted in the backend; only send it without a status
      includeDeleted: filters.statusId === undefined && filters.includeDeleted ? true : undefined,
    }),
    [debouncedSearch, filters.municipalityId, filters.localityId, filters.statusId, filters.includeDeleted]
  );

  const params = useMemo<PreRegisteredAccommodationSearchParams>(
    () => ({ ...filterParams, page, pageSize: ACCOMMODATIONS_PAGE_SIZE }),
    [filterParams, page]
  );

  const query = usePreRegisteredAccommodations(params);

  const changeFilters = (values: AccommodationFilterValues) => {
    setFilters(values);
    setPage(1);
  };

  return { filters, filterParams, changeFilters, page, setPage, query };
};
